// Copyright ©️ 2026 eVolve MEP, LLC

import { useRef, useState, useMemo } from 'react';

import { Divider, Box, type DividerProps, Flex } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';

type Props = {
  onDragDivider: (deltaY: number) => void;
  onDraggingChange?: (dragging: boolean) => void;
  removeVerticalPadding?: boolean;
} & DividerProps;

const dividerThickness = 20;

export const DraggableDivider = ({
  onDragDivider,
  onDraggingChange,
  orientation = 'horizontal',
  removeVerticalPadding = true,
  ...props
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const startClientX = useRef(0);
  const startRelX = useRef(0);
  const startClientY = useRef(0);
  const startRelY = useRef(0);
  const [dragging, setDragging] = useState(false);

  const dividerProps = useMemo<DividerProps>(
    () => ({
      orientation,
      ...(orientation === 'horizontal'
        ? {
            w: '100%',
          }
        : {
            h: '100%',
          }),
      ...props,
    }),
    [orientation, props],
  );

  return (
    <Flex
      align="center"
      justify="center"
      direction={orientation === 'horizontal' ? 'row' : 'column'}
      h={orientation === 'horizontal' ? dividerThickness : undefined}
      w={orientation === 'vertical' ? dividerThickness : undefined}
      mr={removeVerticalPadding && orientation === 'vertical' ? -dividerThickness : undefined}
    >
      <Divider {...dividerProps} />
      <Box
        ref={ref}
        w={20}
        h={20}
        display="flex"
        px={orientation === 'horizontal' ? 'xs' : undefined}
        py={orientation === 'vertical' ? 'xs' : undefined}
        onPointerDown={(e) => {
          e.preventDefault();
          const rect = ref.current?.getBoundingClientRect();
          startClientX.current = e.clientX;
          startClientY.current = e.clientY;
          startRelX.current = e.clientX - (rect?.left ?? 0);
          startRelY.current = e.clientY - (rect?.top ?? 0);
          setDragging(true);
          e.currentTarget.setPointerCapture(e.pointerId);
          onDraggingChange?.(true);
        }}
        onPointerMove={(e) => {
          if (!dragging) return;
          const rect = ref.current?.getBoundingClientRect();
          const relativeX = e.clientX - (rect?.left ?? 0);
          const relativeY = e.clientY - (rect?.top ?? 0);
          const deltaXInside = relativeX - startRelX.current;
          const deltaYInside = relativeY - startRelY.current;
          startClientX.current = e.clientX;
          startClientY.current = e.clientY;
          onDragDivider(orientation === 'horizontal' ? -deltaYInside : deltaXInside);
        }}
        onPointerUp={(e) => {
          setDragging(false);
          onDraggingChange?.(false);
          e.currentTarget.releasePointerCapture(e.pointerId);
        }}
        style={{
          cursor: dragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          touchAction: 'none',
          justifyContent: 'center',
          alignItems: 'center',
          // I don't think this is the right way to do this
          zIndex: orientation === 'vertical' ? 20 : undefined,
        }}
        ml={orientation === 'vertical' ? -18 : undefined}
      >
        <EvolveIcon
          icon="DraggableDivider"
          size="sm"
          color={dragging ? 'gray.9' : 'gray'}
          spanProps={{
            style: {
              transform: orientation === 'vertical' ? 'rotate(90deg)' : undefined,
            },
          }}
        />
      </Box>
      <Divider {...dividerProps} />
    </Flex>
  );
};
